import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import {
  getCrossTenantOrders,
  getSubscriptionAnalytics,
  getPlatformHealth,
} from '../services/adminAnalyticsService';
import { TrendingUp, ShoppingCart, Users, AlertCircle, Loader } from 'lucide-react';

const AnalyticsPage: React.FC = () => {
  const [platformHealth, setPlatformHealth] = useState<any>(null);
  const [orders, setOrders] = useState<any>(null);
  const [subscriptions, setSubscriptions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const [health, ord, sub] = await Promise.all([
          getPlatformHealth(),
          getCrossTenantOrders(),
          getSubscriptionAnalytics(),
        ]);

        setPlatformHealth(health);
        setOrders(ord);
        setSubscriptions(sub);
      } catch (err: any) {
        console.error('Error loading analytics:', err);
        setError(err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  const StatCard = ({
    icon,
    label,
    value,
    color,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    color: string;
  }) => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${color} p-3 rounded-lg`}>{icon}</div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-2">Cross-tenant analytics and platform insights</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="ml-3 text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Platform Health */}
            {platformHealth && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <StatCard
                  icon={<TrendingUp className="w-6 h-6 text-green-600" />}
                  label="Total Revenue"
                  value={`RM ${platformHealth.totalRevenue.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  color="bg-green-100"
                />
                <StatCard
                  icon={<ShoppingCart className="w-6 h-6 text-blue-600" />}
                  label="Total Orders"
                  value={platformHealth.totalOrders.toLocaleString()}
                  color="bg-blue-100"
                />
                <StatCard
                  icon={<Users className="w-6 h-6 text-purple-600" />}
                  label="Active Subscriptions"
                  value={platformHealth.activeSubscriptions}
                  color="bg-purple-100"
                />
                <StatCard
                  icon={<TrendingUp className="w-6 h-6 text-orange-600" />}
                  label="Avg Order Value"
                  value={`RM ${platformHealth.averageOrderValue.toFixed(2)}`}
                  color="bg-orange-100"
                />
              </div>
            )}

            {/* Top Tenants */}
            {platformHealth && platformHealth.topTenants.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Top Restaurants by Revenue</h2>
                <div className="space-y-3">
                  {platformHealth.topTenants.map((tenant: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg font-bold text-gray-900">#{index + 1}</span>
                        <p className="font-medium text-gray-900">{tenant.tenantId}</p>
                      </div>
                      <p className="text-lg font-bold text-green-600">RM {tenant.revenue.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription Analytics */}
            {subscriptions && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Subscription Status</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Active</span>
                      <span className="text-lg font-bold text-green-600">{subscriptions.activeSubscriptions}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Trial</span>
                      <span className="text-lg font-bold text-blue-600">{subscriptions.trialSubscriptions}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Cancelled</span>
                      <span className="text-lg font-bold text-red-600">{subscriptions.cancelledSubscriptions}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Subscription Plans</h3>
                  <div className="space-y-3">
                    {Object.entries(subscriptions.subscriptionsByPlan).map(([plan, count]: [string, any]) => (
                      <div key={plan} className="flex justify-between items-center">
                        <span className="text-gray-600 capitalize">{plan}</span>
                        <span className="text-lg font-bold text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Order Status</h3>
                  <div className="space-y-3">
                    {Object.entries(orders?.ordersByStatus || {}).map(([status, count]: [string, any]) => (
                      <div key={status} className="flex justify-between items-center">
                        <span className="text-gray-600 capitalize">{status}</span>
                        <span className="text-lg font-bold text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AnalyticsPage;

